import { Column, Page, Row } from 'components/shared/Layout';
import Button from 'components/ui/Button';
import moment from 'moment';
import React, { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { GET_USER, GET_VOUCHER_BY_TYPE_AND_PERIOD } from 'store/queries';
import { useManagedMutation, useManagedQuery } from 'utils/managedHooks';
import { useLazyQuery } from '@apollo/client';
import RecipientDetailsForm from './RecipientDetailsForm';
import GenerateDiscountCodeForm from './GenerateDiscountCodeForm';
import CheckBusinessAccount from './CheckBusinessAccount';
import { CREATE_DISCOUNT } from 'store/mutations';
import Modal from 'components/ui/Modal';
import { ACTION_CODES } from 'constants/errors';
import AgentPortalError from 'errors/AgentPortalError';
import {
  carriers,
  classOfServices,
  MAP_CARRIER_AND_COS_TO_PRODUCTS_RESTRICTION,
  INITIAL_MODAL_STATE,
  SILVER_VOUCHER_MONTHLY_BUDGET_ALLOWANCE,
  SILVER_VOUCHER_MONTHLY_NUMBER_ALLOWANCE,
  TRIP_RESTRICTIONS
} from './utils/constant';
import { buildGetVouchersQuery } from './utils/utils';
import config from 'utils/config';
import DiscountCreationUnavailableMessages from './DiscountCreationUnavailableMessages';
import { ErrorMessage } from 'pages/CreateVoucher/CreateVoucher.styles';

const GenerateDiscountCode = () => {
  const isGenerateDiscountCodeEnabled = config('ENABLE_GENERATE_DISCOUNT_CODE', { canOverride: true }) === 'true';
  const { push } = useHistory();
  const [loading, setLoading] = useState(false);
  const [unavailableDiscountCreation, setUnavailableDiscountCreation] = useState(INITIAL_MODAL_STATE);
  const [isValidAccount, setIsValidAccount] = useState({ isActive: false, error: '' });
  const [cachedFormData, cacheFormData] = useState({});
  const {
    data: {
      me: { canGenerateSilverVoucher, paymentCurrencies, email }
    }
  } = useManagedQuery(GET_USER, {
    fetchPolicy: 'cache-only'
  });

  if (!canGenerateSilverVoucher || !isGenerateDiscountCodeEnabled) {
    push('/');
  }

  const [vouchersQuery] = useLazyQuery(GET_VOUCHER_BY_TYPE_AND_PERIOD, {
    fetchPolicy: 'no-cache',
    onCompleted: ({ getVouchersByTypeAndPeriod }) => onVouchersQueryCompleted(getVouchersByTypeAndPeriod),
    onError: (error) => {
      setLoading(false);
      throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
        errorCTARedirect: `/vouchers/generate-discount-code`
      });
    }
  });

  const [createDiscount] = useManagedMutation(CREATE_DISCOUNT, {
    fetchPolicy: 'no-cache',
    onCompleted: ({ createDiscount }) => onCompletedCreateDiscount(createDiscount),
    onError: (error) => {
      setLoading(false);
      throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
        errorCTARedirect: `/vouchers/generate-discount-code`
      });
    }
  });

  const formMethods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange'
  });

  const { handleSubmit, formState, getValues, watch } = formMethods;
  const { errors } = formState;

  const GBValue = watch('GB', false);
  const FRValue = watch('FR', false);
  const BEValue = watch('BE', false);
  const NLValue = watch('NL', false);
  const DEValue = watch('DE', false);
  const isValid = [GBValue, FRValue, BEValue, NLValue, DEValue].filter((item) => item).length >= 2;

  const onHandleBusinessAccount = ({ isActive, error = '' }) => setIsValidAccount({ isActive, error });

  const onSubmit = async (formData) => {
    setLoading(true);
    cacheFormData(formData);
    const variables = buildGetVouchersQuery({ currency: formData.currency });
    return await vouchersQuery({ variables: { ...variables } });
  };

  const onVouchersQueryCompleted = async (vouchersResponse) => {
    const monthlyBudgetReached = vouchersResponse?.totalValueInCurrency >= SILVER_VOUCHER_MONTHLY_BUDGET_ALLOWANCE;
    const monthlyNumberReached = vouchersResponse?.totalCount >= SILVER_VOUCHER_MONTHLY_NUMBER_ALLOWANCE;
    const shouldBlockDiscountCreation = monthlyBudgetReached || monthlyNumberReached;

    if (shouldBlockDiscountCreation) {
      setLoading(false);
      if (monthlyBudgetReached) {
        return setUnavailableDiscountCreation({
          ...INITIAL_MODAL_STATE,
          isOpen: true,
          monthlyBudgetReached
        });
      }

      return setUnavailableDiscountCreation({
        ...INITIAL_MODAL_STATE,
        isOpen: true,
        monthlyNumberReached
      });
    }

    const {
      accountNumber,
      currency,
      recipientEmail,
      firstName,
      lastName,
      salesDateStart,
      salesDateEnd,
      travelDateStart,
      salesTravelsEnd,
      voucherType,
      numberPaxs,
      carrier,
      classOfService,
      tripRestrictions,
      GB,
      FR,
      BE,
      NL,
      DE,
      ES_ER
    } = getValues();
    const countries = [
      { name: 'GB', value: GB },
      { name: 'FR', value: FR },
      { name: 'BE', value: BE },
      { name: 'NL', value: NL },
      { name: 'DE', value: DE },
      { name: 'ES_ER', value: ES_ER }
    ];
    const countriesToSend = countries.filter(({ value }) => value).map(({ name }) => name);

    return await createDiscount({
      variables: {
        type: voucherType,
        currency: currency,
        value: 100,
        recipientEmail,
        firstName,
        lastName,
        productsRestriction: MAP_CARRIER_AND_COS_TO_PRODUCTS_RESTRICTION[carrier][classOfService],
        actionBy: email,
        businessAccount: accountNumber,
        salesPeriodStart: moment(salesDateStart).startOf('day').format('YYYY-MM-DD'),
        travelPeriodStart: moment(travelDateStart).format('YYYY-MM-DD'),
        salesPeriodEnd: moment(salesDateEnd).endOf('day').format('YYYY-MM-DD'),
        travelPeriodEnd: moment(salesTravelsEnd).format('YYYY-MM-DD'),
        maximumNumberOfPaxs: Number(numberPaxs),
        tripRestrictions,
        countriesRestrictions: countriesToSend
      }
    });
  };

  const onCompletedCreateDiscount = ({ alias }) => push(`/discountCodes/${alias}`);
  const onCloseModal = () => setUnavailableDiscountCreation(INITIAL_MODAL_STATE);
  const { isOpen, monthlyBudgetReached, monthlyNumberReached } = unavailableDiscountCreation;

  return (
    <>
      <Modal title={<FormattedMessage id="silverVoucher.unavailable" />} isOpen={isOpen} onClose={onCloseModal}>
        <DiscountCreationUnavailableMessages message={{ monthlyBudgetReached, monthlyNumberReached }} />
      </Modal>
      <FormProvider {...formMethods}>
        <Page columns={20}>
          <Column span={18} start={2}>
            <Row>
              <h1>
                <FormattedMessage id="generateDiscountCode.generate" />
              </h1>
            </Row>

            <form onSubmit={handleSubmit(onSubmit)}>
              <GenerateDiscountCodeForm
                allowedCurrencies={paymentCurrencies}
                NLValue={NLValue}
                DEValue={DEValue}
                isValid={isValid}
                defaultValues={{
                  currency: cachedFormData.currency || paymentCurrencies[0],
                  salesDateStart: cachedFormData.salesDateStart,
                  salesDateEnd: cachedFormData.salesDateEnd,
                  travelDateStart: cachedFormData.travelDateStart,
                  salesTravelsEnd: cachedFormData.salesTravelsEnd,
                  numberPaxs: cachedFormData.numberPaxs || 1,
                  carrier: cachedFormData.carrier || carriers[0].description,
                  classOfService: cachedFormData.classOfService || classOfServices[0].description,
                  tripRestrictions: cachedFormData.tripRestrictions || TRIP_RESTRICTIONS[0].name
                }}
              />

              <CheckBusinessAccount
                onHandleBusinessAccount={onHandleBusinessAccount}
                defaultValues={{
                  accountNumber: cachedFormData.accountNumber,
                  postcode: cachedFormData.postcode
                }}
              />

              {!!isValidAccount.error && <ErrorMessage>{isValidAccount.error}</ErrorMessage>}

              <RecipientDetailsForm
                errors={{
                  firstName: !!errors?.firstName,
                  lastName: !!errors?.lastName,
                  recipientEmail: !!errors?.recipientEmail
                }}
                defaultValues={{
                  firstName: cachedFormData.firstName,
                  lastName: cachedFormData.lastName,
                  recipientEmail: cachedFormData.recipientEmail
                }}
              />
              <Column>
                <Button
                  type="submit"
                  data-testid="submit"
                  data-cy="submit"
                  disabled={!isValidAccount.isActive || !!isValidAccount.error || !isValid || loading}
                  loading={loading}
                >
                  <FormattedMessage id="generateDiscountCode.generate" />
                </Button>
              </Column>
            </form>
          </Column>
        </Page>
      </FormProvider>
    </>
  );
};

export default GenerateDiscountCode;
